import { Modal } from '@components/modal';
import { useFetchTag } from 'api/customProperty/customPropertyService';
import { type Disclosure } from 'interfaces/disclosure/Disclosure.interface';
import { type FC } from 'react';
import Skeleton from 'react-loading-skeleton';

interface DeleteTagModalProps extends Disclosure {
  tagId?: number;
  onConfirm: () => void;
  isLoading?: boolean;
}

const DeleteTagModal: FC<DeleteTagModalProps> = ({
  tagId,
  isOpen,
  handleOpen,
  handleClose,
  onConfirm,
  isLoading = false,
}) => {
  const { data: tagData, isFetching } = useFetchTag(tagId);

  const assets = tagData?.assets ?? [];
  const assetCount = assets.length;
  const displayedAssets = assets.slice(0, 3);
  const remainingCount = Math.max(0, assetCount - 3);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      title="Delete tag"
      maxWidth={500}
      actionBtns={{
        primary: {
          label: 'Delete tag',
          onClick: onConfirm,
          isLoading,
          variant: 'tertiary',
        },
        secondary: {
          label: 'Cancel',
          onClick: handleClose,
          variant: 'secondary',
        },
      }}
    >
      <div className="flex flex-col gap-6">
        {/* Main message */}
        <div className="text-gray-60">
          You are about to delete the tag{' '}
          {isFetching ? (
            <Skeleton width={120} height={20} inline />
          ) : (
            <span className="font-bold text-purple-600">
              {tagData?.property_definition?.display_name ??
                tagData?.property_definition?.name}
            </span>
          )}
        </div>

        {/* Assets information box */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-gray-60 mb-3">
            This tag is linked to{' '}
            {isFetching ? (
              <Skeleton width={40} height={20} inline />
            ) : (
              <span className="font-bold">{assetCount} assets</span>
            )}
          </div>

          {isFetching ? (
            <div className="space-y-2">
              <Skeleton width={150} height={16} />
              <Skeleton width={120} height={16} />
              <Skeleton width={140} height={16} />
            </div>
          ) : (
            <ul className="list-disc list-inside space-y-1 text-gray-60 mb-3">
              {displayedAssets.map((asset) => (
                <li key={asset.id}>{asset.name}</li>
              ))}
              {remainingCount > 0 && (
                <li className="text-gray-500">+ {remainingCount} assets</li>
              )}
            </ul>
          )}

          <div className="text-gray-500 text-sm">
            These assets will lose this tag classification.
          </div>
        </div>

        {/* Warning message */}
        <div className="text-gray-500 italic text-sm">
          This action cannot be undone.
        </div>
      </div>
    </Modal>
  );
};

export default DeleteTagModal;
